<template>
  <div>
    <v-card variant="outlined" class="mt-3">
      <v-card-text>
        <!-- Check type and status chip -->
        <div class="d-flex align-center justify-space-between">
          <span class="text-subtitle-2 font-weight-bold">
            {{ isDeepfakeModel
              ? $t('routes.pod.authenticity.modelDeepfake')
              : $t('routes.pod.authenticity.modelGenai') }}
          </span>
          <v-chip :color="statusColor" variant="flat" size="small">
            {{ statusLabel }}
          </v-chip>
        </div>

        <v-divider class="my-3" />
        <div class="detail-row">
          <span class="text-medium-emphasis">
            {{ isDeepfakeModel
              ? $t('routes.pod.authenticity.scorecard.manipulationScore')
              : $t('routes.pod.authenticity.scorecard.authenticity') }}
          </span>
          <span class="font-weight-bold" :style="{ color: authenticityColor }">
            {{ authenticityPercentage }}%
          </span>
        </div>
        <div class="detail-row">
          <span class="text-medium-emphasis">
            {{ isDeepfakeModel
              ? $t('routes.pod.authenticity.scorecard.manipulationType')
              : $t('routes.pod.authenticity.scorecard.classification') }}
          </span>
          <span>{{ classificationLabel }}</span>
        </div>
        <div v-if="isVideo" class="detail-row">
          <span class="text-medium-emphasis">
            {{ $t('routes.pod.authenticity.scorecard.scanType') }}
          </span>
          <span>{{ scanTypeLabel }}</span>
        </div>

        <!-- Forensic Details (expandable, for videos) -->
        <v-expansion-panels v-if="isVideo && hasForensicContent" variant="accordion" class="mt-3">
          <v-expansion-panel>
            <v-expansion-panel-title class="text-subtitle-2">
              {{ $t('routes.pod.authenticity.forensicDetails') }}
            </v-expansion-panel-title>
            <v-expansion-panel-text>
              <v-table v-if="forensicFrames.length > 0" density="compact">
                <thead>
                  <tr>
                    <th>{{ $t('routes.pod.authenticity.timestamp') }}</th>
                    <th class="text-end">{{ $t('routes.pod.authenticity.aiLikelihood') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(frame, index) in forensicFrames" :key="index">
                    <td>{{ frame.timestamp != null ? formatTimestamp(frame.timestamp) : '-' }}</td>
                    <td class="text-end" :class="{ 'text-error': frameScore(frame) > 0.7 }">
                      {{ (frameScore(frame) * 100).toFixed(0) }}%
                    </td>
                  </tr>
                </tbody>
              </v-table>

              <div v-if="extraForensicKeys.length > 0" class="mt-2">
                <div
                  v-for="key in extraForensicKeys"
                  :key="key"
                  class="d-flex justify-space-between text-body-1 py-1"
                >
                  <span class="font-weight-medium">{{ formatCategoryLabel(key) }}</span>
                  <span class="text-medium-emphasis">{{ check.forensicdetails[key] }}</span>
                </div>
              </div>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>

        <!-- Credits used and completed date -->
        <div v-if="check.creditsused || check.completedat" class="d-flex justify-space-between text-body-2 text-medium-emphasis mt-3">
          <span v-if="check.creditsused">{{ $t('routes.pod.authenticity.creditsUsed') }}: {{ check.creditsused }}</span>
          <span v-else />
          <span v-if="check.completedat">{{ formatDate(check.completedat) }}</span>
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
export default {
  name: 'AuthenticityScorecard',
  props: {
    check: {
      type: Object,
      required: true,
    },
    isVideo: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    isDeepfakeModel() {
      return this.check.forensicdetails?.model === 'deepfake';
    },
    statusLabel() {
      const status = this.check.status;
      const key = `routes.pod.authenticity.status.${status}`;
      return this.$t(key);
    },
    statusColor() {
      switch (this.check.status) {
        case 'AUTHENTIC': return 'success';
        case 'SUSPICIOUS': return 'warning';
        case 'AI_GENERATED': return 'error';
        case 'MANIPULATED': return 'error';
        case 'PENDING': return 'info';
        default: return 'grey';
      }
    },
    authenticityPercentage() {
      if (this.check.overallscore == null) return '-';
      return 100 - this.check.overallscore;
    },
    authenticityColor() {
      if (this.check.overallscore == null) return undefined;
      const pct = this.authenticityPercentage;
      if (pct >= 70) return 'rgb(var(--v-theme-success))';
      if (pct >= 30) return 'rgb(var(--v-theme-warning))';
      return 'rgb(var(--v-theme-error))';
    },
    classificationLabel() {
      if (this.isDeepfakeModel) {
        if (!this.check.manipulationtype) return '-';
        return this.formatCategoryLabel(this.check.manipulationtype);
      }
      if (!this.check.aiclassification) return '-';
      return this.formatCategoryLabel(this.check.aiclassification);
    },
    forensicFrames() {
      const details = this.check.forensicdetails;
      if (details && Array.isArray(details.frames)) {
        return details.frames;
      }
      return [];
    },
    extraForensicKeys() {
      const details = this.check.forensicdetails;
      if (!details || typeof details !== 'object') return [];
      return Object.keys(details).filter((key) => key !== 'frames' && key !== 'summary');
    },
    hasForensicContent() {
      return this.forensicFrames.length > 0 || this.extraForensicKeys.length > 0;
    },
    scanTypeLabel() {
      if (this.check.startsecond != null && this.check.endsecond != null) {
        return this.$t('routes.pod.authenticity.analyzedSegment', {
          start: this.check.startsecond,
          end: this.check.endsecond,
        });
      }
      return this.$t('routes.pod.authenticity.fullScanLabel');
    },
  },
  methods: {
    frameScore(frame) {
      if (this.isDeepfakeModel) {
        return frame.deepfake ?? 0;
      }
      return frame.aiGenerated ?? 0;
    },
    formatTimestamp(ms) {
      const seconds = ms / 1000;
      if (seconds < 60) {
        return seconds.toFixed(1) + 's';
      }
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = Math.round(seconds % 60);
      return `${minutes}m ${remainingSeconds}s`;
    },
    formatDate(dateStr) {
      if (!dateStr) return '';
      const d = new Date(dateStr);
      return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    },
    formatCategoryLabel(key) {
      return key
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (char) => char.toUpperCase());
    },
  },
};
</script>

<style lang="scss" scoped>
.detail-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 0;
}

.w-100 {
  width: 100%;
}
</style>
